<template>
  <v-card rounded="lg">
    <v-toolbar color="primary" density="compact">
      <v-icon :icon="editMode ? 'mdi-pencil-outline' : 'mdi-send-outline'" class="ms-4 me-2"/>
      <v-toolbar-title class="text-subtitle-1 font-weight-medium">
        {{ editMode ? 'Checkliste bearbeiten' : 'Checkliste zuweisen' }}
      </v-toolbar-title>
    </v-toolbar>

    <v-card-text class="pa-4 pa-md-6">
      <!-- Template Info (nur im Zuweisungsmodus) -->
      <v-alert v-if="!editMode" variant="tonal" density="compact" class="mb-5" type="info">
        <div class="font-weight-bold">{{ template?.title }}</div>
        <div v-if="template?.description" class="text-body-2">{{ template.description }}</div>
        <div class="text-caption mt-1">{{ template?.items?.length || 0 }} Prüfpunkte</div>
      </v-alert>

      <!-- Checklist Info (im Bearbeitungsmodus) -->
      <v-alert v-if="editMode" variant="tonal" density="compact" class="mb-5" type="info">
        <div class="font-weight-bold">#{{ checklistId }} — {{ checklistTitle }}</div>
      </v-alert>

      <!--v-select--
          v-model="form.assignees"
          :items="userOptions"
          item-title="full_name"
          item-value="id"
          label="Zuweisen an"
          :variant="useGlobalSetting().globalTextVariant"
          density="compact"
          rounded="lg"
          multiple
          chips
          closable-chips
          :rules="[v => v.length > 0 || 'Mindestens ein Mitarbeiter erforderlich']"
          class="mb-3"
      >
        <template v-slot:prepend-item>
          <v-list-item-title class="text-center font-weight-bold px-4 py-2">
            Mitarbeiter auswählen
          </v-list-item-title>
          <v-divider/>
        </template>
        <template v-slot:item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps" :title="item.raw.full_name" :subtitle="item.raw.id">
            <template v-slot:prepend>
              <v-avatar size="32" color="grey-lighten-3">
                <v-icon icon="mdi-account" size="18"/>
              </v-avatar>
            </template>
          </v-list-item>
        </template>
      </v-select-->

      <!--TODO: checklist: getIcon funtkion für user einbinden bzw erstellen-->
      <UserSelect v-model="form.assignees" label="Zuweisen an" multiple rounded="lg" prepend-inner-icon="mdi-account-multiple"/>

      <!-- Anlage (optional) -->
      <v-text-field
          v-model="form.assetId"
          label="Anlage / AKZ (optional)"
          :variant="useGlobalSetting().globalTextVariant"
          rounded="lg"
          prepend-inner-icon="mdi-factory"
          clearable
      />

      <!-- Auftrag (optional) -->
      <OrderSelect
          v-model="form.orderId"
          label="Auftrag (optional)"
          rounded="lg"
          prepend-inner-icon="mdi-clipboard-text"
          clearable
          class="mb-6"
      />

      <OutageSelect
          v-model="form.outage_code"
          label="Stillstand (optional)"
          rounded="lg"
          prepend-inner-icon="mdi-power"
          clearable
          class="mb-6"
      />

      <!-- Zeitraum -->
      <v-row class="mb-1">
        <v-col cols="12" sm="6">
          <v-text-field
              v-model="form.scheduledStart"
              label="Start"
              :variant="useGlobalSetting().globalTextVariant"
              rounded="lg"
              type="datetime-local"
              prepend-inner-icon="mdi-calendar-start"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
              v-model="form.scheduledEnd"
              label="Ende"
              :variant="useGlobalSetting().globalTextVariant"
              rounded="lg"
              type="datetime-local"
              prepend-inner-icon="mdi-calendar-end"
          />
        </v-col>
      </v-row>

      <!-- Vorschau -->
      <v-expand-transition>
        <v-alert v-if="form.assignees.length > 0 && !editMode" variant="tonal" type="success" density="compact" class="mt-2">
          1 Checkliste wird erstellt für:
          <span class="font-weight-bold">{{ form.assignees.join(', ') }}</span>
        </v-alert>
      </v-expand-transition>
    </v-card-text>

    <!-- Actions -->
    <v-divider/>
    <v-card-actions class="pa-4">
      <v-btn variant="text" @click="$emit('cancel')">Abbrechen</v-btn>
      <v-spacer/>
      <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-send'"
          :disabled="form.assignees.length === 0 || (editMode && saving)"
          :loading="editMode && saving"
          @click="editMode ? saveEdit() : assign()"
      >
        {{ editMode ? 'Speichern' : 'Zuweisen' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {useGlobalSetting} from '@/lib/utils'
import {getUserList} from "@/types/Order";
import {supabase} from "@/api";
import UserSelect from "@/components/inputs/UserSelect.vue";
import OrderSelect from "@/components/inputs/OrderSelect.vue";
import OutageSelect from "@/components/inputs/OutageSelect.vue";

interface ChecklistTemplate {
  id?: number
  title: string
  description: string
  work_group: string | null
  items: { label: string }[]
}

const props = defineProps<{
  template?: ChecklistTemplate | null
  // Edit-Modus Props
  checklistId?: number | null
  checklistTitle?: string
  initialAssignees?: string[]
  initialAssetId?: string | null
  initialOrderId?: number | null
  initialOutageCode?: number | null
  initialScheduledStart?: string | null
  initialScheduledEnd?: string | null
}>()

const editMode = computed(() => !!props.checklistId)

const emit = defineEmits<{
  assign: [data: {
    templateId: number
    assignees: string[]
    scheduledStart: string | null
    scheduledEnd: string | null
    assetId: string | null
    orderId: number | null
    outage_code: number | null
  }]
  cancel: []
  saved: []
}>()

const userOptions = ref<any[]>([])
const saving = ref(false)

const form = ref({
  assignees: [] as string[],
  assetId: '',
  orderId: null as number | null,
  outage_code: null as number | null,
  scheduledStart: '',
  scheduledEnd: ''
})

function assign() {
  if (!props.template?.id || form.value.assignees.length === 0) return

  emit('assign', {
    templateId: props.template.id,
    assignees: form.value.assignees,
    scheduledStart: form.value.scheduledStart || null,
    scheduledEnd: form.value.scheduledEnd || null,
    assetId: form.value.assetId || null,
    orderId: form.value.orderId || null,
    outage_code: form.value.outage_code || null
  })
}

async function saveEdit() {
  if (!props.checklistId || form.value.assignees.length === 0) return
  saving.value = true

  const {error} = await supabase
      .from('checklists')
      .update({
        assignees: form.value.assignees,
        asset_id: form.value.assetId?.trim() || null,
        order_id: form.value.orderId && !isNaN(form.value.orderId) ? form.value.orderId : null,
        scheduled_start: form.value.scheduledStart || null,
        scheduled_end: form.value.scheduledEnd || null
      })
      .eq('id', props.checklistId)

  saving.value = false

  if (error) {
    console.error('Checklist update failed:', error)
    return
  }

  emit('saved')
  emit('cancel')
}

onMounted(async () => {
  userOptions.value = await getUserList()

  if (editMode.value) {
    // Edit-Modus: vorhandene Daten vorausfüllen
    form.value = {
      assignees: props.initialAssignees ? [...props.initialAssignees] : [],
      assetId: props.initialAssetId || '',
      orderId: props.initialOrderId ?? null,
      outage_code: props.initialOutageCode ?? null,
      scheduledStart: props.initialScheduledStart || '',
      scheduledEnd: props.initialScheduledEnd || ''
    }
  } else {
    form.value = {
      assignees: [],
      assetId: '',
      orderId: null,
      outage_code: null,
      scheduledStart: '',
      scheduledEnd: ''
    }
  }
})
</script>
